import React, { useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import { useFormik } from "formik";
import moment from "moment";
import { Moment } from "moment-timezone";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import IPromo_Code from "../../../models/IPromo_Code";
import IProcessPromoCodeDTO from "../../../models/DTO/IProcessPromoCodeDTO";
import IOnProcessPromoCodeDTO from "../../../models/DTO/IOnProcessPromoCodeDTO";
import PriceUpchargeTypeEnum from "../../../models/PriceUpchargeTypeEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import PromoCodeService, { IPromoCodeFormErrors, IPromoCodeFormValues } from "../../../services/PromoCodeService";
import "./PromoCodeSidebar.scss";

type Props = {
    socket: Socket;
    setActivityIndicator: (activityOn: boolean, isInitialLoad?: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    promoCode?: IPromo_Code;
    closeFn: (refreshData?: boolean) => void;
};

const toDateOnly = (value: string | Date | null | undefined): string | null => {
    return value ? moment(value).format("YYYY-MM-DD") : null;
};

const toNullableNumber = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
        return null;
    }

    return Number(value);
};

/** Create / edit form for a single promo code. Field rules live in PromoCodeService.validatePromoCodeForm. */
export const PromoCodeSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const isEdit = !!props.promoCode;

    const [saving, setSaving] = useState(false);
    const [startDate, setStartDate] = useState<Moment | null>(props.promoCode?.start_date ? moment(props.promoCode.start_date) : null);
    const [endDate, setEndDate] = useState<Moment | null>(props.promoCode?.end_date ? moment(props.promoCode.end_date) : null);

    const discountTypeOptions: { title: string; value: number }[] = [
        { title: "percent", value: PriceUpchargeTypeEnum.PERCENT },
        { title: "fixed", value: PriceUpchargeTypeEnum.FIXED },
    ];

    const closeSidebar = () => {
        props.closeFn();
    };

    const onProcessPromoCode = (_data: IOnProcessPromoCodeDTO) => {
        setSaving(false);

        const notification: INotification = {
            title: "successfully_processed_promo_code",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        props.closeFn(true);
    };

    // A Notify (e.g. ERROR_PROMO_CODE_ALREADY_EXISTS) means the save failed: re-enable the form.
    const onNotify = () => {
        setSaving(false);
    };

    const formik = useFormik<IPromoCodeFormValues>({
        initialValues: {
            name: props.promoCode?.name || "",
            code: props.promoCode?.code || "",
            discount_type_id: props.promoCode?.discount_type_id || PriceUpchargeTypeEnum.PERCENT,
            discount_value: props.promoCode?.discount_value ?? "",
            min_order_subtotal: props.promoCode?.min_order_subtotal ?? "",
            max_uses: props.promoCode?.max_uses ?? "",
            single_use_per_customer: props.promoCode ? !!props.promoCode.single_use_per_customer : false,
            start_date: toDateOnly(props.promoCode?.start_date),
            end_date: toDateOnly(props.promoCode?.end_date),
            active: props.promoCode ? !!props.promoCode.active : true,
        },
        validate: (values) => {
            return PromoCodeService.validatePromoCodeForm(values, { timesUsed: props.promoCode?.times_used });
        },
        onSubmit: (values) => {
            const promoCode: IProcessPromoCodeDTO["promoCode"] = {
                ...(props.promoCode ? { id: props.promoCode.id, guid: props.promoCode.guid } : {}),
                name: values.name.trim(),
                code: PromoCodeService.normalizeCode(values.code),
                discount_type_id: Number(values.discount_type_id) as PriceUpchargeTypeEnum,
                discount_value: Number(values.discount_value),
                min_order_subtotal: toNullableNumber(values.min_order_subtotal),
                max_uses: toNullableNumber(values.max_uses),
                single_use_per_customer: !!values.single_use_per_customer,
                start_date: values.start_date || null,
                end_date: values.end_date || null,
                active: !!values.active,
            };

            const payload: IProcessPromoCodeDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                promoCode,
            };

            setSaving(true);
            props.socket.emit(EndpointsEnum.PromoCodesProcessPromoCode, payload);
        },
    });

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PromoCodesOnProcessPromoCode, fn: onProcessPromoCode },
        { endpoint: EndpointsEnum.Notify, fn: onNotify },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const errors = formik.errors as IPromoCodeFormErrors;
    const hasErrors = Object.keys(errors).length > 0;

    const renderError = (field: keyof IPromoCodeFormErrors) => {
        const key = errors[field];

        if (!key || (!formik.touched[field] && !formik.submitCount)) {
            return null;
        }

        return <OwLabel className={"text-danger small"} title={key} noBottomMargin={true} />;
    };

    const setDate = (field: "start_date" | "end_date", date: Moment | null) => {
        if (field === "start_date") {
            setStartDate(date);
        } else {
            setEndDate(date);
        }

        formik.setFieldValue(field, date ? date.format("YYYY-MM-DD") : null);
        formik.setFieldTouched(field, true, false);
    };

    return (
        <div className={"PromoCodeSidebar p-2"}>
            <div>
                <div className={"text-end"}>
                    <OwButton centerIconClass={"fas fa-times"} fn={closeSidebar} className={"btn-danger"} />
                </div>
                <OwTitle title={"promo_code"} />

                <form onSubmit={formik.handleSubmit} data-testid={"promo-code-form"}>
                    <div className="mt-2">
                        <OwLabel title={"name"} showIsReqAsterik={true} />
                        <OwFnInput fn={formik.handleChange} name={"name"} value={formik.values.name} testId={"promo-code-name"} />
                        {renderError("name")}
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"code"} showIsReqAsterik={true} />
                        <OwFnInput
                            fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                                formik.setFieldValue("code", e.target.value.toUpperCase());
                            }}
                            name={"code"}
                            value={formik.values.code}
                            maxLength={32}
                            className={"promo-code-field"}
                            testId={"promo-code-code"}
                        />
                        <OwLabel className={"text-muted small"} title={"promo_code_format_help"} noBottomMargin={true} />
                        {renderError("code")}
                    </div>

                    <div className="mt-2">
                        <OwCheckbox
                            name={"active"}
                            checked={formik.values.active}
                            truthyVal={true}
                            falsyVal={false}
                            fn={formik.handleChange}
                            label={"active"}
                        />
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"discount_type"} showIsReqAsterik={true} />
                        <OwSelectOption
                            fn={formik.handleChange}
                            name={"discount_type_id"}
                            titleProp={"title"}
                            valueProp={"value"}
                            options={discountTypeOptions}
                            selectedOption={formik.values}
                            selectedOptionProp={"discount_type_id"}
                            translateTitle={true}
                        />
                        {renderError("discount_type_id")}
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"discount_value"} showIsReqAsterik={true} />
                        <OwFnInput
                            fn={formik.handleChange}
                            name={"discount_value"}
                            type={"number"}
                            min={0}
                            step={"any"}
                            value={formik.values.discount_value}
                            testId={"promo-code-discount-value"}
                        />
                        {renderError("discount_value")}
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"min_order_subtotal"} />
                        <OwFnInput
                            fn={formik.handleChange}
                            name={"min_order_subtotal"}
                            type={"number"}
                            min={0}
                            step={"any"}
                            value={formik.values.min_order_subtotal ?? ""}
                        />
                        {renderError("min_order_subtotal")}
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"max_uses"} />
                        <OwFnInput
                            fn={formik.handleChange}
                            name={"max_uses"}
                            type={"number"}
                            min={1}
                            step={1}
                            value={formik.values.max_uses ?? ""}
                        />
                        <OwLabel className={"text-muted small"} title={"unlimited"} preText={"∅ = "} noBottomMargin={true} />
                        {renderError("max_uses")}
                    </div>

                    {isEdit ? (
                        <div className="mt-2">
                            <OwLabel title={"times_used"} postText={`: ${props.promoCode?.times_used || 0}`} noBottomMargin={true} />
                        </div>
                    ) : null}

                    <div className="mt-2">
                        <OwCheckbox
                            name={"single_use_per_customer"}
                            checked={formik.values.single_use_per_customer}
                            truthyVal={true}
                            falsyVal={false}
                            fn={formik.handleChange}
                            label={"single_use_per_customer"}
                        />
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"start_date"} />
                        <div className={"d-flex align-items-center gap-1"}>
                            <OwDateInput
                                fn={(date: Moment) => {
                                    setDate("start_date", date);
                                }}
                                selectedDate={startDate}
                                dateFormat={"MM/dd/yyyy"}
                            />
                            {startDate ? (
                                <OwButton
                                    className={"btn-outline-secondary"}
                                    btnSizeClass={"btn-sm"}
                                    centerIconClass={"fas fa-times"}
                                    fn={() => {
                                        setDate("start_date", null);
                                    }}
                                />
                            ) : null}
                        </div>
                        {renderError("start_date")}
                    </div>

                    <div className="mt-2">
                        <OwLabel title={"end_date"} />
                        <div className={"d-flex align-items-center gap-1"}>
                            <OwDateInput
                                fn={(date: Moment) => {
                                    setDate("end_date", date);
                                }}
                                selectedDate={endDate}
                                dateFormat={"MM/dd/yyyy"}
                            />
                            {endDate ? (
                                <OwButton
                                    className={"btn-outline-secondary"}
                                    btnSizeClass={"btn-sm"}
                                    centerIconClass={"fas fa-times"}
                                    fn={() => {
                                        setDate("end_date", null);
                                    }}
                                />
                            ) : null}
                        </div>
                        {renderError("end_date")}
                    </div>

                    <div className="mt-3">
                        <OwButton
                            title={"save"}
                            type={"submit"}
                            active={saving}
                            activeTitle={"please_wait"}
                            disableOnActive={true}
                            disabled={hasErrors && formik.submitCount > 0}
                            testId={"promo-code-save"}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};
